import { useState, useEffect, useCallback } from 'react';
import { GameState, apiService } from '../services/api';

export function useGameState(gameCode: string | null, playerId: string | null) {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGameState = useCallback(async () => {
    if (!gameCode || !playerId) return false;
    
    try {
      setError(null);
      const state = await apiService.getGameState(gameCode, playerId);
      setGameState(state);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch game state');
      return false;
    }
  }, [gameCode, playerId]);

  // Provide a way to update state directly (for action responses)
  const updateGameState = useCallback((newState: GameState) => {
    setGameState(newState);
    setError(null);
  }, []);

  useEffect(() => {
    if (!gameCode || !playerId) return;

    setLoading(true);
    
    const startPolling = async () => {
      // Initial fetch
      const success = await fetchGameState();
      setLoading(false);
      
      // Only start polling if initial fetch succeeded
      if (success) {
        const interval = setInterval(async () => {
          const pollSuccess = await fetchGameState();
          if (!pollSuccess) {
            clearInterval(interval);
          }
        }, 2000);

        return () => clearInterval(interval);
      }
    };

    const cleanup = startPolling();
    return () => {
      cleanup?.then(cleanupFn => cleanupFn?.());
    };
  }, [gameCode, playerId, fetchGameState]);

  return {
    gameState,
    loading,
    error,
    refresh: fetchGameState,
    updateGameState
  };
}